/**
 * FmMapLegend — Operational legend for map layers with value encoding.
 *
 * Props are ALWAYS semantic, never raw color/opacity/hex:
 *  - scale: map scale for reference
 *  - units: what values represent (€/m³, months, %, probability)
 *  - bins: [{ min, max, label, color }] where color is a token var()
 *  - paletteName: semantic palette name (harvest-readiness, hazard-risk, economics-revenue)
 *  - thresholds: optional tick marks between bins
 *  - noDataLabel: label for missing/no-data regions
 *
 * REQUIREMENT: Every map layer that encodes values by color must have a legend.
 * Pages must NEVER pass raw hex/opacity/tile-URL to this component.
 */

import React from 'react';
import './FmMapLegend.css';

export interface FmMapLegendBin {
  /** Minimum value for this bin (inclusive). Optional: numeric range is display-only. */
  min?: number;
  /** Maximum value for this bin (exclusive, except last bin). Optional: display-only. */
  max?: number;
  /** Display label (e.g., "Ready", "Critical", "€50–100") */
  label?: string;
  /** Color token reference (e.g., 'var(--fm-expected)'), never raw hex */
  color?: string;
}

export interface FmMapLegendProps {
  /** Map scale reference (e.g., "1:50000") */
  scale?: string;
  /** Value units (e.g., "€/m³", "months", "%", "probability") */
  units?: string;
  /** Semantic palette name (for accessibility/theming) */
  paletteName?: string;
  /** Color-bin definitions with token-backed colors */
  bins?: FmMapLegendBin[];
  /** Label for missing/no-data areas */
  noDataLabel?: string;
  /** Optional threshold tick marks (numeric values within the bin range) */
  thresholds?: number[];
  /** Optional title/heading for the legend */
  title?: string;
  /** CSS class for custom styling (should be minimal; always use semantic props) */
  className?: string;
  'data-testid'?: string;
}

/**
 * FmMapLegend component — all semantic props, token-backed colors.
 */
export const FmMapLegend: React.FC<FmMapLegendProps> = ({
  scale,
  units,
  paletteName,
  bins,
  noDataLabel,
  thresholds,
  title,
  className,
  'data-testid': testId,
}) => {
  const classes = ['fm-map-legend', className].filter(Boolean).join(' ');

  return (
    <div className={classes} data-testid={testId} data-palette={paletteName}>
      {title && <div className="legend-title">{title}</div>}

      {/* Metadata row: scale and units. Evidence basis lives in FmEvidence. */}
      {(scale || units) && (
        <div className="legend-metadata">
          {scale && <span className="metadata-item">{scale}</span>}
          {units && <span className="metadata-item">{units}</span>}
        </div>
      )}

      {/* Color bins */}
      {bins && bins.length > 0 && (
        <div className="legend-bins">
          {bins.map((bin) => (
            <div key={`${bin.min}-${bin.max}-${bin.label ?? 'bin'}`} className="legend-bin">
              {bin.color && (
                <div className="bin-color" style={{ backgroundColor: bin.color }} />
              )}
              <div className="bin-label">
                {bin.label || (bin.min != null && bin.max != null ? `${bin.min}–${bin.max}` : '')}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No-data indicator */}
      {noDataLabel && (
        <div className="legend-no-data">
          <div className="no-data-pattern" />
          <span>{noDataLabel}</span>
        </div>
      )}

      {/* Thresholds (optional tick marks) */}
      {thresholds && thresholds.length > 0 && (
        <div className="legend-thresholds">
          {thresholds.map((t) => (
            <div key={`threshold-${t}`} className="threshold-mark" title={`Threshold: ${t}`}>
              {t}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FmMapLegend;
